import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";

/** Les objets que la suppression contrôlée sait traiter (mêmes clés qu'en base). */
export type DeletableEntity =
  | "client" | "contact" | "product" | "client_price" | "quote" | "order" | "contract"
  | "lead" | "invoice" | "dispute" | "delivery" | "contract_template" | "tax_regime" | "fee_type" | "payment";

export interface DeletePreview {
  entity: DeletableEntity; id: string; label: string;
  sensitive: boolean; can_delete: boolean; can_archive: boolean; admin: boolean;
  archive_label: string | null; archive_help: string | null;
  blockers: { count: number; label: string }[];
  cascade: { label: string; count: number }[];
  never: string | null; reason_required: boolean;
}

export interface TrashRow {
  id: string; entity: DeletableEntity; label: string; reason: string | null;
  deleted_at: string; purge_after: string; restored_at: string | null; by: string | null; rows: number;
}

/** Tout ce que l'écran doit savoir avant de proposer quoi que ce soit. */
export function useDeletePreview(entity: DeletableEntity, id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["delete_preview", entity, id],
    enabled: Boolean(id) && enabled,
    gcTime: 0, staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("delete_preview", { _entity: entity, _id: id! });
      if (error) throw error;
      return data as unknown as DeletePreview;
    },
  });
}

const INVALIDATE: Record<string, string[]> = {
  client: ["clients", "client", "dashboard"],
  contact: ["contacts", "client"],
  product: ["products", "catalogue"],
  client_price: ["client_prices", "client"],
  quote: ["quotes", "quote", "dashboard"],
  order: ["orders", "order", "dashboard"],
  contract: ["contracts", "contract", "orders", "order", "dashboard"],
  lead: ["leads", "lead", "dashboard"],
  invoice: ["invoices", "invoice", "order", "orders", "dashboard"],
  dispute: ["disputes", "dispute", "leads", "dashboard"],
  delivery: ["deliveries", "order"],
  contract_template: ["contract_templates"],
  tax_regime: ["tax_regimes"],
  fee_type: ["fee_types"],
  payment: ["invoice", "invoices", "payments", "bank", "dashboard"],
};

export function useDeleteExecute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { entity: DeletableEntity; id: string; mode: "archive" | "delete"; reason?: string }) => {
      const { data, error } = await supabase.rpc("delete_execute", { _entity: v.entity, _id: v.id, _mode: v.mode, _reason: v.reason ?? undefined });
      if (error) throw error;
      return data as unknown as { mode: string; label: string; trash_id?: string };
    },
    onSuccess: (d, v) => {
      // La fiche qu'on vient de supprimer ne doit pas être redemandée : on la retire
      // du cache au lieu de la rafraîchir, sinon l'écran encore affiché part en erreur.
      qc.removeQueries({ queryKey: [v.entity, v.id] });
      qc.removeQueries({ queryKey: ["delete_preview", v.entity, v.id] });
      for (const k of [...(INVALIDATE[v.entity] ?? []).filter((k) => k !== v.entity), "trash"]) void qc.invalidateQueries({ queryKey: [k] });
      toast.success(v.mode === "archive" ? `${d.label} archivé` : `${d.label} supprimé — récupérable 30 jours dans la corbeille`);
    },
    onError: (e) => { toast.error((e as { message?: string }).message ?? "Action impossible."); logger.error("delete.execute", e); },
  });
}

export function useTrash() {
  return useQuery({
    queryKey: ["trash"],
    queryFn: async () => { const { data, error } = await supabase.rpc("trash_list", { _limit: 100 }); if (error) throw error; return data as unknown as TrashRow[]; },
  });
}

export function useTrashRestore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { const { data, error } = await supabase.rpc("trash_restore", { _id: id }); if (error) throw error; return data as unknown as { label: string }; },
    onSuccess: (d) => { void qc.invalidateQueries(); toast.success(`${d.label} restauré`); },
    onError: (e) => { toast.error((e as { message?: string }).message ?? "Restauration impossible."); logger.error("trash.restore", e); },
  });
}

/** Suppression en cascade d'un client de test : réservée aux clients en mode test. */
export function usePurgeTestClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { clientId: string; reason: string }) => {
      const { data, error } = await supabase.rpc("purge_test_client", { _client_id: v.clientId, _reason: v.reason });
      if (error) throw error;
      return data as unknown as { label: string; contenu: Record<string, number> };
    },
    onSuccess: (d, v) => {
      qc.removeQueries({ queryKey: ["client", v.clientId] });
      for (const k of ["clients", "quotes", "orders", "contracts", "invoices", "leads", "payments", "dashboard", "trash"]) void qc.invalidateQueries({ queryKey: [k] });
      toast.success(`${d.label} et ses données d'essai supprimés`);
    },
    onError: (e) => { toast.error((e as { message?: string }).message ?? "Suppression impossible."); logger.error("client.purge_test", e); },
  });
}
